import { Box, LinearProgress, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { StatStrip } from "../../../components/UIToolkit/molecules/StatStrip";
import type { PbjPlanCost } from "../../../utils/models";
import { formatCurrency } from "./planValue.utils";

type Props = {
	cost: PbjPlanCost;
	/** Whether the plan has spent anything yet — see `PlanCostPanel`. */
	hasSpend: boolean;
	/** Share of the plan elapsed, already printed as a whole percent. */
	share: string;
};

/**
 * The cost panel's body for a plan with no cuts: the original Investment layout, unchanged — two
 * stats over a single spend bar.
 */
export const PlanInvestmentBody: FC<Props> = ({ cost, hasSpend, share }) => {
	const { t } = useTranslation();
	// The bar measures spend against the WHOLE commitment; the budget-to-date line below it is what
	// `onBudget` is actually judged on, so both are shown rather than collapsing to one.
	const spentPct = cost.totalPrice > 0 ? Math.min(100, Math.max(0, (cost.spendSoFar / cost.totalPrice) * 100)) : 0;

	return (
		<>
			<StatStrip
				dataTestId="pbj-model-plan-cost-stats"
				items={[
					{ label: t("pbj_model.tracker.total_price"), value: formatCurrency(cost.totalPrice), dataTestId: "pbj-model-plan-total-price" },
					{ label: t("pbj_model.tracker.spend_so_far"), value: formatCurrency(cost.spendSoFar), dataTestId: "pbj-model-plan-spend-so-far" },
				]}
			/>

			<Box sx={{ mt: "auto" }}>
				{/* A determinate bar still paints its TRACK, and MUI tints that track with the bar's own
				    colour. At this height a success-tinted track behind a zero-width fill is
				    indistinguishable from a bar filled to 100% — it read as "fully spent" on a plan that
				    has spent nothing. Neutralising the track until there is spend keeps an empty bar
				    looking empty. */}
				<LinearProgress
					variant="determinate"
					value={spentPct}
					color={hasSpend && !cost.onBudget ? "error" : "success"}
					sx={{ height: 8, borderRadius: 4, ...(hasSpend ? {} : { bgcolor: "action.disabledBackground" }) }}
					aria-label={t("pbj_model.tracker.spend_progress_aria")}
					data-testid="pbj-model-plan-spend-bar"
				/>
				<Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
					{t("pbj_model.tracker.budget_to_date", { budget: formatCurrency(cost.budgetToDate), share })}
				</Typography>
				{/* What the figure actually means. Spend is `deliveredToDate x the plan's frozen wage` —
				    the DAY-granular delivery, the same one the progress bar fills to, so the two always
				    agree. Keyed off `hasSpend` rather than off elapsed weeks: zero spend means nothing
				    was delivered above baseline, which is a statement about the department, not about
				    the calendar, and saying "you have not spent" is not the same as "we cannot tell you
				    yet". */}
				<Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.disabled" }} data-testid="pbj-model-plan-spend-basis">
					{t(hasSpend ? "pbj_model.tracker.spend_basis" : "pbj_model.tracker.spend_basis_none")}
				</Typography>
			</Box>
		</>
	);
};
